import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSignals, getPortfolio, getAlpacaStatus, savePreferences } from '../api';

const ETFS = new Set([
  'AGG','BIL','BND','BNDX','EFA','GLD','IAU','IBIT','IEFA','IEMG',
  'IJH','IJR','ITOT','IVE','IVV','IVW','IWD','IWF','IWM','IXUS',
  'QQQ','QQQM','QUAL','RSP','SCHD','SCHF','SCHG','SCHX','SGOV',
  'SPY','SPYM','VB','VCIT','VEA','VEU','VGT','VIG','VO','VOO',
  'VT','VTI','VTV','VUG','VWO','VXUS','VYM','XLK',
]);

const DEFAULTS = {
  defaultQty: 5,
  stockMaxPositionPct: 10, etfMaxPositionPct: 15,
  stockAllocationPct: 60, cashReservePct: 10,
};

export default function AllocationPage() {
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [allSymbols, setAllSymbols] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [savedAllocations, setSavedAllocations] = useState({});
  const [holdings, setHoldings] = useState({});
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('tm_preferences');
    if (stored) try { setPrefs({ ...DEFAULTS, ...JSON.parse(stored) }); } catch {}

    Promise.all([
      getSignals().catch(() => ({ by_consensus: { groups: {} } })),
      getPortfolio().catch(() => ({ positions: [] })),
      getAlpacaStatus().catch(() => null),
    ]).then(([sigData, portData, acctData]) => {
      const groups = sigData?.by_consensus?.groups || {};
      const syms = [];
      Object.values(groups).forEach(arr => arr.forEach(s => {
        if (!syms.find(x => x.symbol === s.symbol)) syms.push(s);
      }));
      setAllSymbols(syms);

      const stocks = syms.filter(s => !ETFS.has(s.symbol));
      const etfs = syms.filter(s => ETFS.has(s.symbol));
      const stockMax = prefs.stockMaxPositionPct || 10;
      const etfMax = prefs.etfMaxPositionPct || 15;
      const a = {};
      stocks.forEach(s => { a[s.symbol] = { catPct: stockMax }; });
      etfs.forEach(s => { a[s.symbol] = { catPct: etfMax }; });
      // Load saved allocations if they exist
      const savedAlloc = prefs.allocations || JSON.parse(localStorage.getItem('tm_allocations') || 'null');
      if (savedAlloc) {
        Object.keys(savedAlloc).forEach(sym => { if (a[sym]) a[sym] = savedAlloc[sym]; });
      }
      setAllocations(a);
      setSavedAllocations(a);

      const pv = Number(acctData?.portfolio_value || 0);
      setPortfolioValue(pv);
      const h = {};
      (portData.positions || []).forEach(p => { h[p.symbol] = { qty: p.qty, value: p.market_value || 0 }; });
      setHoldings(h);
      setLoading(false);
    });
  }, []);

  const stockPct = prefs.stockAllocationPct || 60;
  const cashPct = prefs.cashReservePct || 0;
  const investedPct = 100 - cashPct;
  const etfPct = 100 - stockPct;
  const targetStockPct = Math.round(stockPct * investedPct / 100);
  const targetEtfPct = investedPct - targetStockPct;
  const exclude = new Set((prefs.excludeSymbols || '').split(',').map(s => s.trim()).filter(Boolean));
  const focus = new Set((prefs.focusSymbols || '').split(',').map(s => s.trim()).filter(Boolean));

  let stocks = allSymbols.filter(s => !ETFS.has(s.symbol) && !exclude.has(s.symbol));
  let etfs = allSymbols.filter(s => ETFS.has(s.symbol) && !exclude.has(s.symbol));
  if (focus.size) { stocks = stocks.filter(s => focus.has(s.symbol)); etfs = etfs.filter(s => focus.has(s.symbol)); }
  if (!prefs.enableStocks) stocks = [];
  if (!prefs.enableEtfs) etfs = [];
  stocks.sort((a, b) => (b.composite || 0) - (a.composite || 0));
  etfs.sort((a, b) => (b.composite || 0) - (a.composite || 0));

  // Current allocation totals
  const currentStockValue = stocks.reduce((sum, s) => sum + (holdings[s.symbol]?.value || 0), 0);
  const currentEtfValue = etfs.reduce((sum, s) => sum + (holdings[s.symbol]?.value || 0), 0);
  const currentStockPct = portfolioValue > 0 ? Math.round(currentStockValue / portfolioValue * 1000) / 10 : 0;
  const currentEtfPct = portfolioValue > 0 ? Math.round(currentEtfValue / portfolioValue * 1000) / 10 : 0;
  const heldCount = Object.keys(holdings).length;

  const updateCatPct = (symbol, newCatPct) => {
    setAllocations(prev => ({ ...prev, [symbol]: { catPct: Math.max(0, Math.min(100, newCatPct)) } }));
  };

  const equalizeGroup = (group) => {
    if (!group.length) return;
    const equal = Math.round(100 / group.length * 10) / 10;
    setAllocations(prev => {
      const next = { ...prev };
      group.forEach(s => { next[s.symbol] = { catPct: equal }; });
      return next;
    });
  };

  const dirty = JSON.stringify(allocations) !== JSON.stringify(savedAllocations);

  const handleSave = () => {
    // Only store overrides — symbols that differ from their default max
    const overrides = {};
    Object.entries(allocations).forEach(([sym, val]) => {
      const isEtf = ETFS.has(sym);
      const defaultPct = isEtf ? (prefs.etfMaxPositionPct || 15) : (prefs.stockMaxPositionPct || 10);
      if (val.catPct !== defaultPct) {
        overrides[sym] = val;
      }
    });
    const data = { ...prefs, allocations: overrides };
    localStorage.setItem('tm_allocations', JSON.stringify(overrides));
    savePreferences(data).catch(() => {});
    setSavedAllocations({ ...allocations });
    setToast(`Saved ${Object.keys(overrides).length} custom allocation${Object.keys(overrides).length !== 1 ? 's' : ''}`);
    setTimeout(() => setToast(''), 3000);
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--t3)', fontSize: 14 }}>Loading allocation data...</div>;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Portfolio Allocation</h1>
          <p style={s.sub}>Current holdings vs Max allocation based on your preferences</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to="/preferences" style={s.prefLink}>← Preferences</Link>
          {toast && <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>✓ {toast}</span>}
          <button onClick={handleSave} disabled={!dirty} style={{ ...s.saveBtn, opacity: dirty ? 1 : 0.4 }}>Save</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={s.cards}>
        <div style={{ ...s.card, borderLeft: '3px solid var(--green)' }}>
          <div style={s.cardLabel}>Stocks — Target</div>
          <div style={s.cardValue}>{targetStockPct}%</div>
          <div style={s.cardSub}>Current: {currentStockPct}% · {stocks.filter(sym => holdings[sym.symbol]).length} held</div>
        </div>
        <div style={{ ...s.card, borderLeft: '3px solid #60a5fa' }}>
          <div style={s.cardLabel}>ETFs — Target</div>
          <div style={s.cardValue}>{targetEtfPct}%</div>
          <div style={s.cardSub}>Current: {currentEtfPct}% · {etfs.filter(sym => holdings[sym.symbol]).length} held</div>
        </div>
        <div style={s.card}>
          <div style={s.cardLabel}>Cash Reserve</div>
          <div style={s.cardValue}>{cashPct}%</div>
          <div style={s.cardSub}>Slider: {stockPct}% stocks / {etfPct}% ETFs</div>
        </div>
        <div style={s.card}>
          <div style={s.cardLabel}>Portfolio Value</div>
          <div style={s.cardValue}>${portfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div style={s.cardSub}>{heldCount} positions</div>
        </div>
      </div>

      {/* Current vs Target bar */}
      <div style={s.barSection}>
        <div style={s.barLabel}>
          <span>Current Allocation</span>
          <span style={{ color: 'var(--t3)' }}>{currentStockPct}% stocks · {currentEtfPct}% ETFs · {Math.max(0, 100 - currentStockPct - currentEtfPct).toFixed(1)}% cash</span>
        </div>
        <div style={s.barTrack}>
          <div style={{ ...s.barFill, width: `${currentStockPct}%`, background: 'var(--green)' }} />
          <div style={{ ...s.barFill, width: `${currentEtfPct}%`, background: '#60a5fa' }} />
        </div>
        <div style={{ ...s.barLabel, marginTop: 8 }}>
          <span>Target Allocation</span>
          <span style={{ color: 'var(--t3)' }}>{targetStockPct}% stocks · {targetEtfPct}% ETFs · {cashPct}% cash</span>
        </div>
        <div style={s.barTrack}>
          <div style={{ ...s.barFill, width: `${targetStockPct}%`, background: 'var(--green)', opacity: 0.6 }} />
          <div style={{ ...s.barFill, width: `${targetEtfPct}%`, background: '#60a5fa', opacity: 0.6 }} />
          <div style={{ ...s.barFill, width: `${cashPct}%`, background: 'var(--t3)', opacity: 0.3 }} />
        </div>
      </div>

      {/* Stock table */}
      {stocks.length > 0 && (
        <GroupTable
          label={`Stocks (${targetStockPct}%)`} color="var(--green)" groupPct={targetStockPct}
          items={stocks} allocations={allocations} holdings={holdings}
          portfolioValue={portfolioValue} maxPosPct={prefs.stockMaxPositionPct || 10}
          onUpdate={updateCatPct} onEqualize={() => equalizeGroup(stocks)}
          catLabel="Stocks"
        />
      )}

      {/* ETF table */}
      {etfs.length > 0 && (
        <GroupTable
          label={`ETFs (${targetEtfPct}%)`} color="#60a5fa" groupPct={targetEtfPct}
          items={etfs} allocations={allocations} holdings={holdings}
          portfolioValue={portfolioValue} maxPosPct={prefs.etfMaxPositionPct || 15}
          onUpdate={updateCatPct} onEqualize={() => equalizeGroup(etfs)}
          catLabel="ETFs"
        />
      )}
    </div>
  );
}

function GroupTable({ label, color, groupPct, items, allocations, holdings, portfolioValue, maxPosPct, onUpdate, onEqualize, catLabel }) {
  const totalCatPct = items.reduce((sum, s) => sum + (allocations[s.symbol]?.catPct || 0), 0);
  const overBudget = totalCatPct > 100.5;

  return (
    <div style={s.section}>
      <div style={s.sectionHead}>
        <div>
          <span style={{ ...s.sectionTitle, color }}>{label}</span>
          <span style={s.sectionSub}>{items.length} symbols</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {overBudget && <span style={{ fontSize: 11, color: 'var(--red)' }}>⚠ {totalCatPct.toFixed(1)}% (over 100%)</span>}
          <button onClick={onEqualize} style={s.eqBtn}>Equal Weight</button>
        </div>
      </div>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={{ ...s.th, width: 70 }}>Symbol</th>
            <th style={{ ...s.th, width: 80 }}>Current</th>
            <th style={{ ...s.th, width: 140 }}>Max % of {catLabel}</th>
            <th style={{ ...s.th, width: 100 }}>Max % Portfolio</th>
          </tr>
        </thead>
        <tbody>
          {items.map((sym, i) => {
            const catPct = allocations[sym.symbol]?.catPct || 0;
            const portPct = Math.round(catPct * groupPct / 100 * 10) / 10;
            const overMax = portPct > maxPosPct;
            const h = holdings[sym.symbol];
            const currentPct = portfolioValue > 0 && h ? Math.round((h.value / portfolioValue) * 1000) / 10 : 0;
            return (
              <tr key={sym.symbol} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--bg-2)' }}>
                <td style={{ ...s.td, fontWeight: 600 }}>{sym.symbol}</td>
                <td style={s.td}>
                  {h ? (
                    <span style={{ fontSize: 11 }}>
                      <span style={{ fontWeight: 600 }}>{h.qty}</span>
                      <span style={{ color: 'var(--t3)', marginLeft: 4 }}>({currentPct}%)</span>
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--t3)' }}>—</span>
                  )}
                </td>
                <td style={s.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" value={catPct} min={0} max={100} step={0.5}
                      onChange={e => onUpdate(sym.symbol, Number(e.target.value) || 0)}
                      style={s.pctInput} aria-label={`${sym.symbol} category %`} />
                    <span style={{ fontSize: 10, color: 'var(--t3)' }}>%</span>
                    <div style={s.miniBar}>
                      <div style={{ ...s.miniBarFill, width: `${Math.min(catPct, 100)}%`, background: color }} />
                    </div>
                  </div>
                </td>
                <td style={{ ...s.td, fontWeight: 600, color: overMax ? 'var(--red)' : 'var(--t1)' }}>
                  {portPct.toFixed(1)}%
                  {overMax && <span style={{ fontSize: 9, marginLeft: 4 }}>⚠</span>}
                </td>
              </tr>
            );
          })}
          {items.length > 0 && (
            <tr style={{ background: 'var(--bg-3)', fontWeight: 700 }}>
              <td style={s.td}>Total</td>
              <td style={s.td}></td>
              <td style={{ ...s.td, color: overBudget ? 'var(--red)' : 'var(--t1)' }}>{totalCatPct.toFixed(1)}%</td>
              <td style={s.td}>{(totalCatPct * groupPct / 100).toFixed(1)}%</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  h1: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--t3)' },
  prefLink: {
    fontSize: 12, color: 'var(--t3)', textDecoration: 'none', padding: '6px 14px',
    background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6,
  },
  saveBtn: {
    padding: '7px 18px', background: 'var(--green)', color: '#000', border: 'none',
    borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },

  cards: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 },
  card: {
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
    padding: '14px 18px',
  },
  cardLabel: { fontSize: 10, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  cardValue: { fontSize: 20, fontWeight: 700, color: 'var(--t1)' },
  cardSub: { fontSize: 11, color: 'var(--t3)', marginTop: 3 },

  barSection: {
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
    padding: '16px 20px', marginBottom: 20,
  },
  barLabel: { display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, marginBottom: 6 },
  barTrack: { display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: 'var(--bg-3)' },
  barFill: { height: '100%', transition: 'width 0.2s' },

  section: {
    background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)',
    marginBottom: 16, overflow: 'hidden',
  },
  sectionHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderBottom: '1px solid var(--border-s)',
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, marginRight: 8 },
  sectionSub: { fontSize: 11, color: 'var(--t3)' },
  eqBtn: {
    padding: '4px 12px', background: 'var(--bg-2)', color: 'var(--t2)',
    border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 16px', fontSize: 10, fontWeight: 600,
    color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid var(--border)', background: 'var(--bg-2)',
  },
  td: { padding: '7px 16px', borderBottom: '1px solid var(--border-s)', fontSize: 12 },
  pctInput: {
    width: 50, padding: '4px 6px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 12, textAlign: 'center',
  },
  miniBar: { flex: 1, height: 4, background: 'var(--bg-3)', borderRadius: 2, overflow: 'hidden' },
  miniBarFill: { height: '100%', borderRadius: 2, transition: 'width 0.15s' },
};
